import { LocateFixed } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiErrorMessage } from '../../api/client'
import { applyPendingOnboarding, savePendingOnboarding } from '../../auth/pendingOnboarding'
import { useAuth } from '../../auth/AuthContext'
import { JoinCodeStep } from '../../components/auth/JoinCodeStep'
import { ErrorMessage } from '../../components/ErrorMessage'
import { OnboardingShell } from '../../components/OnboardingShell'
import { TermsCheckbox } from '../../components/TermsCheckbox'
import { useTaxonomy } from '../../taxonomy/TaxonomyContext'
import type { NeedCategory, RegisterRequest } from '../../types/api'
import { VISIBLE_LOOKING_FOR } from '../../utils/lookingFor'
import { fillIfEmpty, reverseGeocode } from '../../utils/reverseGeocode'
import { isValidMobile, isValidPincode, MOBILE_PATTERN, PINCODE_PATTERN } from '../../utils/validation'

export function GuardianRegisterForm({
  onBack,
  referralCode,
  inviteToken,
}: {
  onBack: () => void
  referralCode?: string
  /** Present when the parent arrived from the invite email; claims the waiting link on register. */
  inviteToken?: string
}) {
  const { register, verifyRegistration } = useAuth()
  const navigate = useNavigate()
  const { itemsOf } = useTaxonomy()

  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [pincode, setPincode] = useState('')
  const [locality, setLocality] = useState('')
  const [lookingFor, setLookingFor] = useState<Set<NeedCategory>>(new Set())
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null)
  const [locating, setLocating] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  // The request a join code was sent for. Set means the code step is showing; the form state above
  // is kept, so "Change email" goes back to a filled-in form.
  const [sentBody, setSentBody] = useState<RegisterRequest | null>(null)

  function toggleLookingFor(category: NeedCategory) {
    setLookingFor((prev) => {
      const next = new Set(prev)
      if (next.has(category)) next.delete(category)
      else next.add(category)
      return next
    })
  }

  /**
   * Pins the dot and fills the pincode and locality from it.
   *
   * <p>Only fills what is still empty, so a parent who has typed their own area keeps it. A
   * failed lookup keeps the pin and says so - the coordinates are the half the map needs, and the
   * pincode can still be typed.
   */
  function useCurrentLocation() {
    if (!navigator.geolocation) return
    setLocating(true)
    setError(null)
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords
        setCoords({ lat: latitude, lng: longitude })
        try {
          const found = await reverseGeocode(latitude, longitude)
          setPincode((p) => (/^\d{6}$/.test(p) ? p : found.pincode ?? ''))
          setLocality((v) => fillIfEmpty(v, found.area ?? found.city))
        } catch (err) {
          console.error('[guardian-register] could not look up the address', err)
          setError('Location pinned, but the address could not be looked up - please type it in.')
        } finally {
          setLocating(false)
        }
      },
      () => {
        setLocating(false)
        setError('Could not get your location. Check the browser has permission, or type it in.')
      },
      { timeout: 8000 },
    )
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)

    if (!isValidMobile(phone)) {
      setError('Enter a valid 10-digit mobile number')
      return
    }
    if (!isValidPincode(pincode)) {
      setError('Enter a valid 6-digit pincode - it places your dot on the map')
      return
    }
    if (!locality.trim()) {
      setError('Enter your area / locality - it is shown beside your dot')
      return
    }
    if (lookingFor.size === 0) {
      setError('Choose at least one thing your child is looking for')
      return
    }
    if (!termsAccepted) {
      setError('Accept the Terms & Conditions and Privacy Policy to continue')
      return
    }

    setSubmitting(true)
    try {
      const body: RegisterRequest = {
        entityKind: 'GUARDIAN',
        persona: 'DEMAND',
        fullName,
        email,
        phone,
        locality: locality.trim(),
        // Both guaranteed present by the checks above, so no `|| undefined` fallback that would
        // quietly let a blank through if a check were ever removed.
        pincode,
        referralCode,
        inviteToken,
        termsAccepted,
      }
      await register(body)

      // Stashed rather than sent: nothing exists until the join code is entered - see
      // pendingOnboarding. Written straight after, in verify().
      savePendingOnboarding({
        profile: coords ? { lat: coords.lat, lng: coords.lng } : undefined,
        // The chip only states a category - the server requires at least one item too (see
        // TaxonomyValidator). The taxonomy's first item for that category is a real, valid
        // stand-in, editable any time from "What my child wants to learn" on the profile.
        needs: [...lookingFor].map((category) => {
          const items = itemsOf(category)
          return {
            category,
            items: items.length ? [items[0]] : undefined,
            isPrimary: lookingFor.size === 1,
          }
        }),
      })

      setSentBody(body)
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not join the network. Please try again.'))
    } finally {
      setSubmitting(false)
    }
  }

  /** Step 2: the code creates the account (or adds the role to an existing one) and signs in. */
  async function verify(code: string) {
    await verifyRegistration(email, code)
    await applyPendingOnboarding()
    navigate('/dashboard', { replace: true })
  }

  if (sentBody) {
    return (
      <OnboardingShell title="Create your guardian account">
        <JoinCodeStep
          sentTo={sentBody.email}
          onVerify={verify}
          onResend={async () => {
            await register(sentBody)
          }}
          onBack={() => setSentBody(null)}
          submitLabel="Create account"
          buttonClassName="btn-sun w-full"
        />
      </OnboardingShell>
    )
  }

  return (
    <OnboardingShell title="Create your guardian account">
      <form onSubmit={onSubmit} className="space-y-4">
        {inviteToken ? (
          <div className="rounded-xl border border-sun-400/60 bg-sun-100 px-3 py-2.5 text-sm text-ink-800">
            You were invited by your child. Create your account and their link request will be
            waiting on your dashboard for you to approve or decline.
          </div>
        ) : (
          <p className="text-sm text-ink-600">
            If your child already registered with their own mobile number, you can link them from
            your Guardian page after signing in.
          </p>
        )}

        <label className="block">
          <span className="label label-required">Full name</span>
          <input required id="fullName" name="name" autoComplete="name" value={fullName} onChange={(e) => setFullName(e.target.value)} className="input" />
        </label>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="label label-required">Email address</span>
            <input required id="email" name="email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input" />
          </label>
          <label className="block">
            <span className="label label-required">Mobile number</span>
            <input required id="phone" name="tel" type="tel" inputMode="numeric" autoComplete="tel" pattern={MOBILE_PATTERN} maxLength={10} title="Enter a valid 10-digit mobile number" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} className="input" />
          </label>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="label label-required">Location pincode</span>
            <input
              required
              id="pincode"
              name="postal-code"
              autoComplete="postal-code"
              inputMode="numeric"
              pattern={PINCODE_PATTERN}
              maxLength={6}
              title="Enter a valid 6-digit pincode"
              value={pincode}
              onChange={(e) => setPincode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="input"
              placeholder="201001"
            />
            <span className="mt-1 block text-xs text-ink-400">Places your dot on the map</span>
          </label>
          <label className="block">
            <span className="label label-required">Area / Locality</span>
            <input id="locality" name="locality" autoComplete="address-level3" value={locality} onChange={(e) => setLocality(e.target.value)} className="input" />
          </label>
        </div>

        <button
          type="button"
          onClick={useCurrentLocation}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink-700 underline-offset-4 hover:underline"
        >
          <LocateFixed size={15} /> {locating ? 'Locating...' : coords ? 'Location set' : 'Use my current location'}
        </button>

        <div>
          <span className="label label-required">What is your child looking for?</span>
          <div className="flex flex-wrap gap-2">
            {VISIBLE_LOOKING_FOR.map((opt) => (
              <button
                key={opt.category}
                type="button"
                onClick={() => toggleLookingFor(opt.category)}
                className={`chip ${lookingFor.has(opt.category) ? 'chip-active' : ''}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-ink-200 bg-ink-50 p-3 text-sm text-ink-600">
          <p className="font-semibold text-ink-900">What you&apos;ll be able to do</p>
          <p className="mt-1">
            Link your children, approve every connection request they send or receive, and see
            their activity. Your child will see that you are linked.
          </p>
        </div>

        <TermsCheckbox checked={termsAccepted} onChange={setTermsAccepted} />

        <ErrorMessage message={error} />

        <div className="flex items-center justify-between pt-2">
          <button type="button" onClick={onBack} className="btn-ghost">Back</button>
          <button type="submit" disabled={submitting} className="btn-sun">
            {submitting ? 'Creating account...' : 'Join the network'}
          </button>
        </div>
      </form>
    </OnboardingShell>
  )
}
